import { useState } from 'react'

import { useActionless } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import { CardType, defaultCard } from 'types/deck'
import { Combo } from 'services/apiTypes/spellbook.types'
import { generateTabState } from 'types/searchV2'

import PhatButton from 'components/formElements/PhatButton'
import ImageCard from 'components/card/deckCards/imageCard'
import CardModalStandalone from 'components/card/CardModal'
import CardEditionsGridOverlay from 'components/card/CardEditionsGridOverlay'
import Link from 'components/elements/Link'
import Icon from 'components/elements/Icon'
import Image from 'components/elements/Image'
import { StandaloneCardGridOverlay } from 'components/deckPage/modals/cardComparisonOverlay'

import { CardText } from 'components/misc/customIcons/ManaSymbol/helpers'

import styles from './spellbookCombo.module.scss'

type Props = {
  combo: Combo
  cards: (CardType | undefined)[]

  onCardUpdated: (currentId: string, updatedCard: CardType) => Promise<any>
}

const SpellbookCombo = ({ combo, cards, onCardUpdated }: Props) => {
  const [selectedCard, setSelectedCard] = useState<CardType>(defaultCard)
  const [editionsAsDefault, setEditionsAsDefault] = useState(false)
  const [relatedCardIds, setRelatedCardIds] = useState(new Array<string>())

  const [setActiveState, setDeckState] = useActionless(SET_ACTIVE_STATE, SET_DECK_STATE)

  const handleCardClicked = (card: CardType, forceAllPrintingsView?: boolean) => {
    setSelectedCard(card)
    setEditionsAsDefault(!!forceAllPrintingsView)
  }

  const handleDismissSelectedCard = () => {
    setSelectedCard(defaultCard)
    setEditionsAsDefault(false)
  }

  const handleExtrasClick = (query: string) => {
    const newSearchTab = generateTabState.scrySearch()

    // @ts-ignore
    newSearchTab.meta.query = query

    setActiveState({ searchOpen: newSearchTab })
  }

  const comboComplete = cards.every(card => card?.qty || card?.otherPrintingQty)

  return (
    <>
      <div className={styles.container}>
        <div className={styles.controls}>
          <span className={styles.status}>
            {comboComplete ? (
              <>
                <Icon name="check circle" className={styles.green} />
                Complete
              </>
            ) : (
              <>
                <Icon name="times circle" className={styles.red} />
                Incomplete
              </>
            )}
          </span>

          <Link href={`https://commanderspellbook.com/combo/${combo.id}/`} target="_blank" rel="noopener noreferrer">
            <PhatButton color="blue">
              <Image
                src="/images/spellbook_simple.svg"
                alt="Commander Spellbook logo"
                className={styles.spellbookLogo}
              />
              Combo on Commander Spellbook
            </PhatButton>
          </Link>
        </div>

        <div className={styles.cards}>
          {cards.map((card, index) => {
            if (!card) return null

            return (
              <ImageCard
                noMultiSelect
                standaloneCard={card}
                onCardUpdated={updatedCard => onCardUpdated(card.id, updatedCard)}
                // // //   className={styles.card}    // // //

                // className={styles.imageDeckCard}
                // wrapperClassName={styles.inDeckCard}
                // basicCardClass={styles.imageDeckCard}
                // imageClassName={styles.basicCard}
                key={card.id}
                cardId={card.id}
                actionsOverrides={{
                  onCardClick: () => null,
                  openCardModal: openToEditionsGrid => handleCardClicked(card, openToEditionsGrid),
                  onDragStart: () => setActiveState({ hideStackOverlay: true }),
                  onDragEnd: () => setActiveState({ hideStackOverlay: false }),
                  onViewRelatedCards: () =>
                    card.relatedCardIds.length === 1
                      ? setDeckState({ menuCardId: card.relatedCardIds[0] })
                      : setRelatedCardIds(card.relatedCardIds),
                }}
              />
            )
          })}

          {combo.extraRequirements.length > 0 && (
            <div className={styles.extraRequirements}>
              <h3>Extra Requirements</h3>

              <div className={styles.extraRequirementsList}>
                {combo.extraRequirements.map((req, index) => (
                  <button key={index} onClick={() => handleExtrasClick(req.syntaxQuery)}>
                    <span>•</span>
                    <span> {req.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.comboInfo}>
          <div className={styles.infoBlock}>
            <h3>Steps</h3>

            <ol>
              {combo.steps.map((step, index) => (
                <li key={`${combo.id}-step-${index}`}>
                  <CardText input={step} />
                </li>
              ))}
            </ol>
          </div>

          <div className={styles.infoBlock}>
            <h3>Effects</h3>

            <ul>
              {combo.comboEffects.map((effect, index) => (
                <li key={`${combo.id}-effect-${index}`}>{effect}</li>
              ))}
            </ul>
          </div>

          <div className={styles.infoBlock}>
            <h3>Prerequisites</h3>

            <ul>
              {combo.prerequisites.length === 0 && <li>None</li>}

              {combo.prerequisites.map((prerequisite, index) => (
                <li key={`${combo.id}-prerequisite-${index}`}>{prerequisite}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <CardModalStandalone
        open={!!selectedCard.id && !editionsAsDefault}
        card={selectedCard}
        onCardUpdated={updatedCard => onCardUpdated(selectedCard.id, updatedCard)}
        onClose={handleDismissSelectedCard}
      />

      <CardEditionsGridOverlay
        open={!!selectedCard.id && editionsAsDefault}
        card={selectedCard}
        onCardSelected={updatedCard => onCardUpdated(selectedCard.id, updatedCard)}
        onClose={handleDismissSelectedCard}
      />

      <StandaloneCardGridOverlay
        header="In deck matches"
        open={!!relatedCardIds.length}
        onClose={() => setRelatedCardIds([])}
        cardIds={relatedCardIds}
      />
    </>
  )
}

export default SpellbookCombo
